/**
 * @fileoverview Job Overview (Plan Phase)
 *
 * Purpose: Displays key job information captured during creation —
 * details, appointment info, key dates, and team members — followed
 * by a condensed cross-agent overview that pulls in each summarisation
 * agent's existing detail panel inline. Each section header deep-links to
 * the corresponding agent detail view in the Gather → Summaries tab.
 *
 * Layout: the page is split into underline tabs so it stays digestible as
 * it grows. The leading "Overview" tab stacks every section (with group
 * headings) so the full picture is still on one screen; the remaining tabs
 * isolate each section —
 *   • Overview             — Details, Background, Outcome, Assets,
 *                            Creditors (unsecured + preferred),
 *                            Investigations, Notes. Security Interests,
 *                            Loans, and ATO/SRO are not shown on this page.
 *   • Details              — Details / Appointment / Team cards
 *   • Background           — AI "mini wiki" (JobOverviewSummary)
 *   • Estimated Outcome    — the cross-agent outcome waterfall
 *   • Assets               — AssetListPanel (compact)
 *   • Creditors            — Creditors / Preferred Creditors
 *   • Investigations       — read-only snapshots of the Review & Analysis
 *                            agents (Books & Records, Solvency Analysis,
 *                            Recoverable Transactions, Civil & Criminal
 *                            Offences), each linking to its full workspace
 *   • Notes                — free-form job journal (JobNotesFeed)
 *
 * URL: /dashboard/jobs/:jobId/plan
 *
 * Sections rendered below "Team":
 *   • Estimated Outcome — outer `h2` + EstimatedOutcomeWaterfall (derived
 *     surplus/shortfall; practitioner fee estimate editable inline).
 *   Below that, only for agents that have either a task row or a registered manifest:
 *   1. Assets              — AssetListPanel (compact)
 *   2. Creditors           — GeneralLiabilities / EmployeeEntitlements
 *   3. Investigations      — snapshots of the Review & Analysis agents
 *
 * Each section card uses the same chrome (icon, title, status pill,
 * "View detail" link). The embedded panels are rendered in `compact` mode
 * — i.e. with their stat-cards / total-bars / add-buttons hidden — so the
 * Overview shows just the data tables. Practitioners click through to the
 * full detail view (in Gather → Summaries) for analysis and editing.
 *
 * When the underlying agent has no task row yet OR is in `waiting`, we
 * show a friendly placeholder instead of forcing the panel to render
 * against an empty taskId. Failed states surface a brief error with a
 * deep-link to the detail page so the practitioner can retry.
 *
 * Dependencies:
 * - Convex: jobs.queries.getJob, summarization.publicQueries.getSummarizationTasksByJob
 * - Manifests: convex/jobs/summarization/agents/uiMetadata
 * - Embedded Panels: assetList, employment, generalLiabilities
 * - Overview: EstimatedOutcomeWaterfall, EntityDirectorsDetail (plan phase
 *   only), InvestigationsSection
 */

import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { AppointmentType, CompanyNameVariantType } from "@cvx/jobs/schema";
import { formatAdditionalPlaceOfBusiness } from "@cvx/lib/placesOfBusiness";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import {
  Loader2,
  Building2,
  Calendar,
  Users,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronRight,
  Package,
  Receipt,
  AlertTriangle,
  ArrowRight,
  ClipboardList,
  type LucideIcon,
} from "lucide-react";
import { ConvertPreAppointmentDialog } from "./-ui.convert-pre-appointment-dialog";
import { ALL_MANIFEST_UI_METADATA } from "@cvx/jobs/summarization/agents/uiMetadata";
import { agentIdToSlug } from "@/components/jobs/phases/gather/summarization/agentRegistry";
import { AssetListPanel } from "@/components/jobs/phases/gather/summarization/assetList";
import { EmployeeEntitlementsPanel } from "@/components/jobs/phases/gather/summarization/employment";
import { LiabilitiesPanel } from "@/components/jobs/phases/gather/summarization/generalLiabilities";
import { InvestigationsSection } from "@/components/jobs/phases/overview/investigations/InvestigationsSection";
import {
  CollapsibleCard,
  CopyableValue,
  EntityDirectorsDetail,
  EstimatedOutcomeWaterfall,
  JobOverviewSummary,
} from "@/components/jobs/phases/overview";
import { JobNotesFeed } from "@/components/jobs/notes/JobNotesFeed";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import { caseTypeFromAppointmentType } from "@/lib/precedent-templates";
import { isBankruptcyAppointmentType } from "@cvx/lib/appointmentTypes";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/jobs/$jobId/plan",
)({
  // `tab` deep-links straight to one of the page's tabs — used by timesheet
  // evidence links to open a file note's source on the Notes tab (MOR-1041).
  validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
    tab: typeof search.tab === "string" ? search.tab : undefined,
  }),
  component: PlanPhase,
});

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const APPOINTMENT_TYPE_LABELS: Record<string, string> = {
  cvl: "Creditors Voluntary Liquidation (CVL)",
  mvl: "Members Voluntary Liquidation (MVL)",
  simplified_liquidation: "Simplified Liquidation",
  court_liquidation: "Court Liquidation",
  provisional_liquidation: "Provisional Liquidation",
  sbr: "Small Business Restructuring (SBR)",
  voluntary_administration: "Voluntary Administration (VA)",
  deed_of_company_arrangement: "Deed of Company Arrangement (DOCA)",
  receivership: "Receivership",
  scheme_of_arrangement: "Scheme of Arrangement",
  personal_bankruptcy: "Personal Bankruptcy",
  business_bankruptcy: "Business Bankruptcy",
  creditors_petition: "Creditors Petition",
  debtors_petition: "Debtors Petition",
  part_x_arrangement: "Personal Insolvency Agreement (Part X)",
};

const COMPANY_NAME_VARIANT_TYPE_LABELS: Record<CompanyNameVariantType, string> =
  {
    trading_name: "Trading name",
    trust_name: "Trust name",
    former_trading_name: "Former trading name",
  };

// MOR-340 — human-readable labels for the appointed company's trustee
// capacity, surfaced in the overview when a trust is captured.
const TRUSTEE_CAPACITY_LABELS: Record<
  "sole_trustee" | "dual_capacity" | "unknown",
  string
> = {
  sole_trustee: "Sole trustee",
  dual_capacity: "Dual capacity (own right and as trustee)",
  unknown: "Trustee (capacity unconfirmed)",
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TaskRow = {
  _id: Id<"summarizationTasks">;
  agentId: string;
  agentName: string;
  status: string;
  documentCount: number;
  documentsAnalysed: number;
  concernCount: number;
  error?: string;
};

type AgentStatus = "waiting" | "running" | "completed" | "failed" | "cancelled";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Formats an ISO date string (YYYY-MM-DD) to "27 March 2026".
 */
function formatDate(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return iso;

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return `${day} ${months[month - 1]} ${year}`;
}

function normalizeStatus(raw: string | undefined): AgentStatus {
  switch (raw) {
    case "completed":
    case "running":
    case "failed":
    case "cancelled":
    case "waiting":
      return raw;
    default:
      return "waiting";
  }
}

// ---------------------------------------------------------------------------
// Shared card styling (matches SummarizationView group cards)
// ---------------------------------------------------------------------------

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function copyableFieldValue(value?: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (
    trimmed.length === 0 ||
    trimmed === "—" ||
    trimmed === "Not yet recorded"
  ) {
    return null;
  }
  return trimmed;
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  const display = value && value.trim().length > 0 ? value : "—";
  return (
    <div className="group/copy flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <CopyableValue value={copyableFieldValue(value)} label={label}>
        <span className="text-sm">{display}</span>
      </CopyableValue>
    </div>
  );
}

/** MOR-989 — every additional place of business, one line each. */
function AdditionalPlacesOfBusinessDetail({
  places,
}: {
  places?: Array<{
    street: string;
    suburb: string;
    state: string;
    postcode: string;
    includeInDocuments: boolean;
  }>;
}) {
  const formatted = (places ?? [])
    .map(formatAdditionalPlaceOfBusiness)
    .filter(Boolean);

  if (formatted.length === 0) {
    return <DetailRow label="Additional Places of Business" value={null} />;
  }

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">
        Additional Places of Business
      </span>
      <div className="flex flex-col gap-1">
        {formatted.map((address, index) => (
          <span key={`${address}-${index}`} className="text-sm">
            {address}
          </span>
        ))}
      </div>
    </div>
  );
}

function CompanyNameVariantsDetail({
  variants,
}: {
  variants?: Array<{
    name: string;
    type: CompanyNameVariantType;
    includeInDocuments: boolean;
  }>;
}) {
  const includedVariants = (variants ?? []).filter(
    (variant) => variant.includeInDocuments,
  );

  if (includedVariants.length === 0) {
    return <DetailRow label="Additional Names" value={null} />;
  }

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">Additional Names</span>
      <div className="flex flex-col gap-1">
        {includedVariants.map((variant, index) => (
          <CopyableValue
            key={`${variant.type}-${variant.name}-${index}`}
            value={variant.name}
            label={COMPANY_NAME_VARIANT_TYPE_LABELS[variant.type]}
          >
            <span className="text-sm">
              <span>{variant.name}</span>
              <span className="ml-2 text-xs text-muted-foreground">
                {COMPANY_NAME_VARIANT_TYPE_LABELS[variant.type]}
              </span>
            </span>
          </CopyableValue>
        ))}
      </div>
    </div>
  );
}

/**
 * Compact status pill rendered next to each section title. Mirrors the
 * iconography from SummarizationView so the two views feel consistent.
 */
function StatusPill({ status }: { status: AgentStatus }) {
  switch (status) {
    case "completed":
      return (
        <span className="flex items-center gap-1 text-xs text-emerald-600">
          <CheckCircle2 className="h-3 w-3" />
          Completed
        </span>
      );
    case "running":
      return (
        <span className="flex items-center gap-1 text-xs text-blue-500">
          <Loader2 className="h-3 w-3 animate-spin" />
          Running
        </span>
      );
    case "failed":
      return (
        <span className="flex items-center gap-1 text-xs text-destructive">
          <XCircle className="h-3 w-3" />
          Failed
        </span>
      );
    case "cancelled":
      return (
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <XCircle className="h-3 w-3" />
          Cancelled
        </span>
      );
    case "waiting":
    default:
      return (
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          Waiting for documents
        </span>
      );
  }
}

interface SectionShellProps {
  icon: LucideIcon;
  title: string;
  status: AgentStatus;
  /** Deep-link to the agent detail in Gather → Summaries (when a task exists). */
  onViewDetail?: () => void;
  /** When provided, replaces the embedded panel (used for waiting/failed). */
  placeholder?: React.ReactNode;
  children?: React.ReactNode;
}

/**
 * Standard chrome for an Overview section. Shared so every embedded panel
 * gets the same header treatment regardless of which agent it represents.
 */
function SectionShell({
  icon: Icon,
  title,
  status,
  onViewDetail,
  placeholder,
  children,
}: SectionShellProps) {
  return (
    <div className={GROUP_CARD_CLASS}>
      <div className="flex items-center justify-between gap-3 px-5 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
          <h4 className="truncate text-sm font-semibold">{title}</h4>
          <span className="ml-2">
            <StatusPill status={status} />
          </span>
        </div>
        {onViewDetail && (
          <Button
            variant="ghost"
            size="sm"
            className="-mr-2 gap-1 text-xs text-muted-foreground"
            onClick={onViewDetail}
          >
            View detail
            <ChevronRight className="h-3 w-3" />
          </Button>
        )}
      </div>
      <div className="border-t border-border">{placeholder ?? children}</div>
    </div>
  );
}

/**
 * Friendly placeholder when an agent hasn't run yet (or has been
 * cancelled). We deliberately don't render the heavy panel for an empty
 * task because most panels assume `taskId` is a real document id.
 */
function EmptyState({
  status,
  error,
}: {
  status: AgentStatus;
  error?: string;
}) {
  if (status === "failed") {
    return (
      <div className="flex flex-col items-start gap-1 px-5 py-8 text-sm">
        <span className="flex items-center gap-1.5 font-medium text-destructive">
          <AlertTriangle className="h-4 w-4" />
          This summary failed to generate
        </span>
        {error && (
          <span className="line-clamp-2 text-xs text-muted-foreground">
            {error}
          </span>
        )}
      </div>
    );
  }
  if (status === "cancelled") {
    return (
      <p className="px-5 py-8 text-sm text-muted-foreground">
        This summary was cancelled.
      </p>
    );
  }
  return (
    <p className="px-5 py-8 text-sm text-muted-foreground">
      Waiting for documents in the relevant categories. The agent will run
      automatically as soon as the first matching document is indexed.
    </p>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function PlanPhase() {
  const { jobId } = Route.useParams();
  const { tab: initialTab } = Route.useSearch();
  const navigate = useNavigate();
  const [convertOpen, setConvertOpen] = useState(false);

  const { data: job, isLoading } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId: jobId as Id<"jobs"> }),
  );

  const { data: tasksData } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.getSummarizationTasksByJob,
      { jobId: jobId as Id<"jobs"> },
    ),
  );

  // Note count powers the badge on the Notes tab so the number of entries is
  // visible without opening the tab.
  const { data: notes } = useQuery(
    convexQuery(api.jobs.notes.listJobNotes, { jobId: jobId as Id<"jobs"> }),
  );
  const notesCount = notes?.length ?? 0;

  const tasksByAgentId = useMemo(() => {
    const map = new Map<string, TaskRow>();
    for (const t of (tasksData ?? []) as ReadonlyArray<TaskRow>) {
      map.set(t.agentId, t);
    }
    return map;
  }, [tasksData]);

  const isViewer = job?.userRole === "viewer";

  /**
   * Navigates the user to the per-agent detail page in the Data section.
   * Resolves the agent slug from the task's agentId via the shared registry
   * and routes to /summaries/$agent. No-op if the slug is unknown (which
   * shouldn't happen unless the registry drifts from the manifest list).
   */
  const goToAgentDetail = (taskId: Id<"summarizationTasks">) => {
    const task = (tasksData ?? []).find(
      (t: { _id: Id<"summarizationTasks"> }) => t._id === taskId,
    );
    if (!task) return;
    const slug = agentIdToSlug(task.agentId);
    if (!slug) return;
    navigate({
      to: "/dashboard/jobs/$jobId/summaries/$agent",
      params: { jobId, agent: slug },
    });
  };

  /**
   * Same deep-link as `goToAgentDetail`, but keyed directly by the agent's
   * manifest id. Used by the Estimated Outcome waterfall, whose lines are
   * derived from agent data rather than backed by a specific task row.
   */
  const goToAgentDetailById = (agentId: string) => {
    const slug = agentIdToSlug(agentId);
    if (!slug) return;
    navigate({
      to: "/dashboard/jobs/$jobId/summaries/$agent",
      params: { jobId, agent: slug },
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!job) return null;

  // Extract individual agent tasks so each section is one lookup.
  const assetsTask = tasksByAgentId.get("sum_asset_list");
  const employmentTask = tasksByAgentId.get("sum_employment");
  const liabilitiesTask = tasksByAgentId.get("sum_general_liabilities");

  const isPreAppointment = job.status === "pre_appointment";
  const canConvert =
    isPreAppointment && (job.userRole === "owner" || job.userRole === "member");

  // Personal-case appointments (personal + business bankruptcy) describe an
  // individual, so the corporate identity fields (Company Name, ACN, registered
  // office, principal place of business) don't apply. Swap the Details grid.
  const isPersonalCase =
    caseTypeFromAppointmentType(job.appointmentType) === "personal";
  // A Part X debtor is never made bankrupt (MOR-1838), so the individual and
  // their estate are labelled "Debtor" rather than "Bankrupt".
  const individualNoun = isBankruptcyAppointmentType(job.appointmentType)
    ? "Bankrupt"
    : "Debtor";
  const residentialAddress = job.registeredOfficeStreet
    ? [
        job.registeredOfficeStreet,
        job.registeredOfficeSuburb,
        job.registeredOfficeState,
        job.registeredOfficePostcode,
      ]
        .filter(Boolean)
        .join(", ")
    : job.registeredAddress;
  const courtValue = [job.courtName, job.courtState].filter(Boolean).join(", ");

  // Each section is defined once and rendered both in its own tab and,
  // stacked, in the "Overview" tab. Radix mounts only the active tab's
  // content, so reusing the same element in two places is safe.
  const detailsContent = (
    <>
      {/* Details */}
      <CollapsibleCard
        icon={Building2}
        title="Details"
        defaultOpen
        persistId="plan:details"
      >
        <div className="px-5 py-4">
          {isPersonalCase ? (
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <DetailRow
                label={`${individualNoun} Name`}
                value={job.bankruptName ?? job.entityName}
              />
              <DetailRow
                label={`${individualNoun} Estate Name`}
                value={job.entityName}
              />
              <DetailRow label="Job Name" value={job.jobName} />
              <CompanyNameVariantsDetail variants={job.companyNameVariants} />
              {job.abn && <DetailRow label="ABN" value={job.abn} />}
              <DetailRow
                label="Administration Number"
                value={job.administrationNumber}
              />
              {courtValue && <DetailRow label="Court" value={courtValue} />}
              {job.sequestrationNumber && (
                <DetailRow
                  label="Court File No."
                  value={job.sequestrationNumber}
                />
              )}
              {residentialAddress && (
                <DetailRow
                  label="Residential Address"
                  value={residentialAddress}
                />
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <DetailRow label="Company Name" value={job.entityName} />
              <DetailRow label="Job Name" value={job.jobName} />
              <CompanyNameVariantsDetail variants={job.companyNameVariants} />
              <EntityDirectorsDetail jobId={jobId as Id<"jobs">} />
              <DetailRow label="ACN" value={job.acn} />
              <DetailRow label="ABN" value={job.abn} />
              {/* MOR-340 — trust details. Surfaced whenever a trust is captured
                  (at job setup or later in Settings) so the overview reflects the
                  trust entity + its ABN. For a sole-trustee company the company
                  ABN above is intentionally blank; the ABN lives on the trust. */}
              {(job.trustName || job.trustAbn) && (
                <>
                  <DetailRow label="Trust Name" value={job.trustName} />
                  <DetailRow label="Trust ABN" value={job.trustAbn} />
                  {job.trusteeCapacity && (
                    <DetailRow
                      label="Trustee Capacity"
                      value={TRUSTEE_CAPACITY_LABELS[job.trusteeCapacity]}
                    />
                  )}
                </>
              )}
              <DetailRow label="Registered Office" value={residentialAddress} />
              {(job.principalPlaceStreet ||
                job.principalPlaceSuburb ||
                job.principalPlaceState ||
                job.principalPlacePostcode) && (
                <DetailRow
                  label="Principal Place of Business"
                  value={[
                    job.principalPlaceStreet,
                    job.principalPlaceSuburb,
                    job.principalPlaceState,
                    job.principalPlacePostcode,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                />
              )}
              {(job.additionalPlacesOfBusiness?.length ?? 0) > 0 && (
                <AdditionalPlacesOfBusinessDetail
                  places={job.additionalPlacesOfBusiness}
                />
              )}
            </div>
          )}
        </div>
      </CollapsibleCard>

      {/* Appointment Details */}
      <CollapsibleCard
        icon={Calendar}
        title="Appointment Details"
        defaultOpen
        persistId="plan:appointment-details"
      >
        <div className="px-5 py-4">
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            <DetailRow
              label="Appointment Type"
              value={
                job.appointmentType
                  ? (APPOINTMENT_TYPE_LABELS[job.appointmentType] ??
                    job.appointmentType)
                  : "Not yet recorded"
              }
            />
            <DetailRow
              label="Date of Appointment"
              value={
                job.appointmentDate
                  ? formatDate(job.appointmentDate)
                  : "Not yet recorded"
              }
            />
          </div>
        </div>
      </CollapsibleCard>

      {/* Team */}
      <CollapsibleCard
        icon={Users}
        title="Team"
        defaultOpen
        persistId="plan:team"
      >
        <div className="space-y-4 px-5 py-4">
          {/* Appointees */}
          <div className="space-y-1.5">
            <span className="text-xs text-muted-foreground">Appointees</span>
            {job.appointees && job.appointees.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {job.appointees.map((name: string) => (
                  <Badge key={name} variant="default" size="sm">
                    {name}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No appointees designated.
              </p>
            )}
          </div>

          {/* Team Members */}
          <div className="space-y-1.5">
            <span className="text-xs text-muted-foreground">Members</span>
            {job.practitioners.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {job.practitioners.map((name: string) => (
                  <Badge key={name} variant="secondary" size="sm">
                    {name}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No team members added.
              </p>
            )}
          </div>
        </div>
      </CollapsibleCard>
    </>
  );

  const backgroundContent = <JobOverviewSummary jobId={jobId as Id<"jobs">} />;

  const outcomeContent = (
    <EstimatedOutcomeWaterfall
      jobId={jobId as Id<"jobs">}
      estimatedPractitionerFees={job.estimatedPractitionerFees}
      canEditFees={!isViewer}
      onViewAgentDetail={goToAgentDetailById}
    />
  );

  const assetsContent = (
    <AssetsSection
      jobId={jobId as Id<"jobs">}
      task={assetsTask}
      isViewer={isViewer}
      onViewDetail={goToAgentDetail}
    />
  );

  // Unsecured creditors plus preferred (priority) creditors. Shown under
  // a "Creditors" heading on Overview and as the dedicated Creditors tab.
  const creditorsContent = (
    <>
      <GeneralLiabilitiesSection
        jobId={jobId as Id<"jobs">}
        task={liabilitiesTask}
        isViewer={isViewer}
        onViewDetail={goToAgentDetail}
      />

      <EmployeeEntitlementsSection
        jobId={jobId as Id<"jobs">}
        task={employmentTask}
        isViewer={isViewer}
        onViewDetail={goToAgentDetail}
      />
    </>
  );

  // Investigations — a read-only snapshot of each Review & Analysis agent
  // (Books & Records, Solvency Analysis, Recoverable Transactions, Civil &
  // Criminal Offences), each deep-linking to its full workspace.
  const investigationsContent = (
    <InvestigationsSection jobId={jobId as Id<"jobs">} />
  );

  const notesContent = (
    <div className={GROUP_CARD_CLASS}>
      <div className="px-5 py-4">
        <JobNotesFeed jobId={jobId as Id<"jobs">} readOnly={isViewer} />
      </div>
    </div>
  );

  /** Group heading used to delineate stacked sections in the Overview tab. */
  const sectionHeading = (label: string) => (
    <h2 className="pt-4 text-lg font-semibold tracking-tight">{label}</h2>
  );

  return (
    <div className="flex flex-1 flex-col gap-5 bg-muted p-6">
      {/* Page Header */}
      <h1 className="text-2xl font-semibold tracking-tight">Overview</h1>

      {/* Pre-appointment banner — Flow A jobs awaiting the instrument */}
      {isPreAppointment && (
        <div className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 text-amber-900 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <ClipboardList className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="flex flex-col gap-1">
              <p className="text-sm font-semibold">Pre-appointment job</p>
              <p className="text-xs">
                The firm has not yet been formally appointed. Run searches and
                prepare engagement letters now; convert this job once the Notice
                of Appointment is received.
              </p>
            </div>
          </div>
          {canConvert && (
            <Button
              type="button"
              size="sm"
              className="gap-1 self-start sm:self-auto"
              onClick={() => setConvertOpen(true)}
            >
              Convert to Appointment
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}

      <ConvertPreAppointmentDialog
        jobId={jobId as Id<"jobs">}
        initialAppointmentType={
          job.appointmentType
            ? (job.appointmentType as AppointmentType)
            : undefined
        }
        open={convertOpen}
        onOpenChange={setConvertOpen}
      />

      {/* Tabs group the overview into digestible sections rather than one
          long scroll. Underline style (no icons) mirrors the create-job
          review dialog. The leading "Overview" tab stacks every section so
          the full picture is still available on one screen. */}
      <Tabs
        // Remount when the link target changes so a deep link to another
        // tab takes effect even if this page is already open.
        key={initialTab ?? "overview"}
        defaultValue={initialTab ?? "overview"}
        className="flex min-w-0 flex-1 flex-col"
      >
        <TabsList
          variant="underline"
          className="w-full shrink-0 flex-nowrap justify-start gap-3 overflow-x-auto [scrollbar-width:none]"
        >
          <TabsTrigger
            variant="underline"
            value="overview"
            className="shrink-0"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger variant="underline" value="details" className="shrink-0">
            Details
          </TabsTrigger>
          <TabsTrigger
            variant="underline"
            value="background"
            className="shrink-0"
          >
            Background
          </TabsTrigger>
          {!isPreAppointment && (
            <>
              <TabsTrigger
                variant="underline"
                value="outcome"
                className="shrink-0"
              >
                Estimated Outcome
              </TabsTrigger>
              <TabsTrigger
                variant="underline"
                value="assets"
                className="shrink-0"
              >
                Assets
              </TabsTrigger>
              <TabsTrigger
                variant="underline"
                value="creditors"
                className="shrink-0"
              >
                Creditors
              </TabsTrigger>
              <TabsTrigger
                variant="underline"
                value="investigations"
                className="shrink-0"
              >
                Investigations
              </TabsTrigger>
            </>
          )}
          <TabsTrigger
            variant="underline"
            value="notes"
            className="shrink-0 gap-1.5"
          >
            Notes
            {notesCount > 0 && (
              <Badge variant="secondary" size="sm" className="px-1.5">
                {notesCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Overview — every section stacked, with group headings to
            delineate the financial summaries. */}
        <TabsContent value="overview" className="flex flex-col gap-5">
          {detailsContent}
          {backgroundContent}
          {/* Estimated Outcome, Assets, Creditors, and Investigations are
              driven by summarisation agents that don't run for pre-appointment
              (Flow A) jobs, so they're hidden until the job is converted. */}
          {!isPreAppointment && (
            <>
              {sectionHeading("Estimated Outcome")}
              {outcomeContent}
              {sectionHeading("Assets")}
              {assetsContent}
              {sectionHeading("Creditors")}
              {creditorsContent}
              {sectionHeading("Investigations")}
              {investigationsContent}
            </>
          )}
          {sectionHeading("Notes")}
          {notesContent}
        </TabsContent>

        {/* Details — entity, appointment, and team cards. */}
        <TabsContent value="details" className="flex flex-col gap-5">
          {detailsContent}
        </TabsContent>

        {/* Background — AI-generated "mini wiki" (MOR-485). */}
        <TabsContent value="background" className="flex flex-col gap-5">
          {backgroundContent}
        </TabsContent>

        {/* Estimated Outcome, Assets, Creditors, and Investigations tabs are
            only rendered for appointed (non pre-appointment) jobs. */}
        {!isPreAppointment && (
          <>
            {/* Estimated Outcome — cross-agent summary: realisable assets →
                practitioner fees → priority → unsecured deductions and the
                bottom-line surplus / shortfall. Expand/collapse per section
                inside the table. */}
            <TabsContent value="outcome" className="flex flex-col gap-5">
              {outcomeContent}
            </TabsContent>

            {/* Assets */}
            <TabsContent value="assets" className="flex flex-col gap-5">
              {assetsContent}
            </TabsContent>

            {/* Creditors — unsecured plus preferred (priority) creditors. */}
            <TabsContent value="creditors" className="flex flex-col gap-5">
              {creditorsContent}
            </TabsContent>

            {/* Investigations — snapshots of the Review & Analysis agents. */}
            <TabsContent value="investigations" className="flex flex-col gap-5">
              {investigationsContent}
            </TabsContent>
          </>
        )}

        {/* Notes — free-form job journal (post / edit / delete, author-only),
            reusing the shared notes feed used by contacts and tasks. */}
        <TabsContent value="notes" className="flex flex-col gap-5">
          {notesContent}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section components
// ---------------------------------------------------------------------------
//
// Each section follows the same shape: read its task, derive a status,
// either render the matching agent panel (when there's a real task) or
// surface a friendly placeholder. The section title pulls from the
// manifest registry so labels stay in sync with the Gather → Summaries
// view automatically.
//
// `onViewDocument` for the panels that need it is a no-op here — clicking
// a document inside the embedded panel jumps to the per-agent detail
// view, which already wires the document drawer correctly.

function manifestName(agentId: string, fallback: string): string {
  return (
    ALL_MANIFEST_UI_METADATA.find((m) => m.id === agentId)?.name ?? fallback
  );
}

interface SectionProps {
  jobId: Id<"jobs">;
  task: TaskRow | undefined;
  isViewer?: boolean;
  onViewDetail: (taskId: Id<"summarizationTasks">) => void;
}

function AssetsSection({ jobId, task, isViewer, onViewDetail }: SectionProps) {
  const status = normalizeStatus(task?.status);
  const showPanel =
    !!task &&
    status !== "waiting" &&
    status !== "failed" &&
    status !== "cancelled";
  return (
    <SectionShell
      icon={Package}
      title={manifestName("sum_asset_list", "Assets")}
      status={status}
      onViewDetail={task ? () => onViewDetail(task._id) : undefined}
      placeholder={
        !showPanel ? (
          <EmptyState status={status} error={task?.error} />
        ) : undefined
      }
    >
      <div className="px-5 py-4">
        {task && (
          <AssetListPanel
            jobId={jobId}
            taskId={task._id}
            isRunning={status === "running"}
            isViewer={isViewer}
            compact
          />
        )}
      </div>
    </SectionShell>
  );
}

function EmployeeEntitlementsSection({
  jobId,
  task,
  isViewer,
  onViewDetail,
}: SectionProps) {
  const status = normalizeStatus(task?.status);
  const showPanel =
    !!task &&
    status !== "waiting" &&
    status !== "failed" &&
    status !== "cancelled";
  return (
    <SectionShell
      icon={Users}
      title={manifestName("sum_employment", "Preferred Creditors")}
      status={status}
      onViewDetail={task ? () => onViewDetail(task._id) : undefined}
      placeholder={
        !showPanel ? (
          <EmptyState status={status} error={task?.error} />
        ) : undefined
      }
    >
      <div className="px-5 py-4">
        {task && (
          <EmployeeEntitlementsPanel
            jobId={jobId}
            taskId={task._id}
            isRunning={status === "running"}
            isViewer={isViewer}
            compact
          />
        )}
      </div>
    </SectionShell>
  );
}

function GeneralLiabilitiesSection({
  jobId,
  task,
  isViewer,
  onViewDetail,
}: SectionProps) {
  const status = normalizeStatus(task?.status);
  const showPanel =
    !!task &&
    status !== "waiting" &&
    status !== "failed" &&
    status !== "cancelled";
  return (
    <SectionShell
      icon={Receipt}
      title={manifestName("sum_general_liabilities", "Creditors")}
      status={status}
      onViewDetail={task ? () => onViewDetail(task._id) : undefined}
      placeholder={
        !showPanel ? (
          <EmptyState status={status} error={task?.error} />
        ) : undefined
      }
    >
      <div className="px-5 py-4">
        {task && (
          <LiabilitiesPanel
            jobId={jobId}
            taskId={task._id}
            isRunning={status === "running"}
            isViewer={isViewer}
            compact
          />
        )}
      </div>
    </SectionShell>
  );
}
